import { cva, type VariantProps } from 'class-variance-authority';

/**
 * TocNav variant styles using Class Variance Authority (CVA).
 *
 * Defines container density / padding for the horizontal "in this article"
 * navigation bar. All styles use design tokens from the HEN Design System.
 *
 * Two variant maps:
 * - `tocNavVariants` — outer `<nav>` container. Just full width and the
 *   white surface. **No padding** — vertical and horizontal padding both
 *   live on the inner content rows so the 3px reading-progress strip sits
 *   flush at the bottom edge and reaches edge-to-edge horizontally.
 * - `tocNavContentVariants` — inner content wrapper that holds the
 *   controls row and the disclosure panel. The `padded` variant applies
 *   `px-page-sides` so the wrapper aligns with the article hero / page
 *   chrome at every breakpoint; `flush` zeros out the side padding for
 *   embedding inside a parent that already manages its own spacing.
 *   Vertical padding is applied per-row by the component (see
 *   `tocNavRowVariants`) so the disclosure panel can drop the row gap
 *   when collapsed.
 *
 * ## Variants
 * - padded (default): page-sides horizontal padding on the inner wrapper.
 *   Drop directly below `ArticleHeroBanner`.
 * - flush: zero horizontal padding — for embedding inside a parent that
 *   already manages its own spacing.
 */
export const tocNavVariants = cva('w-full bg-white');

export const tocNavContentVariants = cva('', {
  variants: {
    padding: {
      padded: 'px-page-sides',
      flush: '',
    },
  },
  defaultVariants: {
    padding: 'padded',
  },
});

/**
 * Shared classes for the inner rows (controls Flex + disclosure region).
 * `max-w-content mx-auto` caps the line length and centers the row inside
 * the page-sides padded wrapper above. Vertical padding is applied via
 * the `padding` variant on the row that needs it (the controls row gets
 * `py-md` always; the disclosure region gets `pb-md` only when expanded
 * so the bar collapses snugly when closed).
 */
export const tocNavRowVariants = cva('w-full max-w-content mx-auto', {
  variants: {
    spacing: {
      controls: 'py-xs',
      disclosure: 'pb-md',
      none: '',
    },
  },
  defaultVariants: {
    spacing: 'none',
  },
});

export type TocNavVariantProps = VariantProps<typeof tocNavContentVariants>;
